import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Phone, Envelope, Storefront, Clock, CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = {
  pending:   { label: "Pendiente",          color: "#FFC000" },
  contacted: { label: "En contacto",        color: "#1E88E5" },
  converted: { label: "Convertida a nota",  color: "#00873D" },
  rejected:  { label: "Rechazada",          color: "#E0000F" },
};

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCur = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${fmt(n)}`;

export default function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/portal-orders", { params: { status: filter } });
    setOrders(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (oid, status) => {
    try {
      await api.patch(`/portal-orders/${oid}`, { status });
      toast.success("Estado actualizado");
      load();
      setDetail(null);
    } catch (err) { toast.error("Error"); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header>
        <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">PORTAL DE REVENDEDORES</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Solicitudes</h1>
        <p className="text-zinc-500 mt-1 text-sm">Pedidos enviados por tus revendedores desde su portal.</p>
      </header>

      <div className="flex gap-2 flex-wrap">
        {[{v:"pending",l:"Pendientes"},{v:"contacted",l:"En contacto"},{v:"converted",l:"Convertidas"},{v:"rejected",l:"Rechazadas"},{v:"all",l:"Todas"}].map(f => (
          <button key={f.v} onClick={()=>setFilter(f.v)} className={`text-xs px-3 py-1.5 rounded-sm border ${filter===f.v ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`} data-testid={`filter-${f.v}`}>{f.l}</button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="portal-orders-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Contacto</th>
              <th className="text-right px-4 py-3">Items</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-400 dotted-bg text-xs">Sin solicitudes en este filtro</td></tr>}
            {orders.map(o => (
              <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={()=>setDetail(o)} data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 text-zinc-600 text-xs">{new Date(o.created_at).toLocaleString("es-MX")}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{o.client_name}</div>
                  {o.client_business && <div className="text-[11px] text-zinc-500 inline-flex items-center gap-0.5"><Storefront size={10}/>{o.client_business}</div>}
                </td>
                <td className="px-4 py-3 text-[11px] text-zinc-600">
                  {o.client_phone && <div className="inline-flex items-center gap-1"><Phone size={10}/>{o.client_phone}</div>}
                  {o.client_email && <div className="inline-flex items-center gap-1"><Envelope size={10}/>{o.client_email}</div>}
                </td>
                <td className="px-4 py-3 text-right numeric text-zinc-600">{o.items.length}</td>
                <td className="px-4 py-3 text-right numeric font-bold">{fmtCur(o.total, o.currency)}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-semibold px-2 py-1 rounded" style={{ background: STATUS[o.status].color+"20", color: STATUS[o.status].color }}>
                    {STATUS[o.status].label}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-zinc-400"><ArrowRight size={14}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o)=>{ if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Solicitud de {detail.client_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-[10px] font-mono-data uppercase text-zinc-500">Fecha</div>{new Date(detail.created_at).toLocaleString("es-MX")}</div>
                  <div>
                    <div className="text-[10px] font-mono-data uppercase text-zinc-500">Estado</div>
                    <span className="text-[11px] font-semibold px-2 py-1 rounded" style={{ background: STATUS[detail.status].color+"20", color: STATUS[detail.status].color }}>{STATUS[detail.status].label}</span>
                  </div>
                  <div><div className="text-[10px] font-mono-data uppercase text-zinc-500">Teléfono</div>{detail.client_phone || "—"}</div>
                  <div><div className="text-[10px] font-mono-data uppercase text-zinc-500">Email</div>{detail.client_email || "—"}</div>
                  <div><div className="text-[10px] font-mono-data uppercase text-zinc-500">Descuento aplicado</div>{detail.discount_pct || 0}%</div>
                  <div><div className="text-[10px] font-mono-data uppercase text-zinc-500">Total</div><span className="numeric font-bold">{fmtCur(detail.total, detail.currency)}</span></div>
                </div>
                <div className="border border-zinc-200">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                      <tr><th className="text-left px-3 py-2">Producto</th><th className="text-right px-3 py-2">Cant.</th><th className="text-right px-3 py-2">P.U.</th><th className="text-right px-3 py-2">Subtotal</th></tr>
                    </thead>
                    <tbody>
                      {detail.items.map((it, i) => (
                        <tr key={`${it.product_id || "i"}-${i}`} className="border-t border-zinc-100">
                          <td className="px-3 py-2">{it.product_name}<div className="text-[11px] text-zinc-500">{it.brand}</div></td>
                          <td className="px-3 py-2 text-right numeric">{it.qty}</td>
                          <td className="px-3 py-2 text-right numeric">
                            {fmtCur(it.unit_price, it.currency)}
                            {it.marketplace_price > it.unit_price && <div className="text-[10px] text-zinc-400 line-through numeric">{fmtCur(it.marketplace_price, it.currency)}</div>}
                          </td>
                          <td className="px-3 py-2 text-right numeric font-semibold">{fmtCur(it.subtotal, it.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detail.notes && <div className="text-xs text-zinc-600 border-l-2 border-zinc-300 pl-3">{detail.notes}</div>}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {detail.status === "pending" && (
                  <Button onClick={()=>updateStatus(detail.id, "contacted")} className="bg-[#1E88E5] hover:bg-[#1565C0] text-white gap-1" data-testid="mark-contacted"><Clock size={14}/>Marcar en contacto</Button>
                )}
                {detail.status !== "converted" && detail.status !== "rejected" && (
                  <>
                    <Button onClick={()=>updateStatus(detail.id, "converted")} className="bg-[#00873D] hover:bg-[#006e30] text-white gap-1" data-testid="mark-converted"><CheckCircle size={14}/>Convertir / Vendida</Button>
                    <Button variant="outline" onClick={()=>updateStatus(detail.id, "rejected")} className="text-[#E0000F] border-[#E0000F]/30 gap-1" data-testid="mark-rejected"><XCircle size={14}/>Rechazar</Button>
                  </>
                )}
                <Button variant="outline" onClick={()=>setDetail(null)}>Cerrar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
